import { FC } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  Divider,
  Flex,
  Heading,
  HStack,
  Img,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import { de } from 'date-fns/locale';
import { CommentForm } from '../Forms/CommentForm';
import { CommentWithChildren } from '../Paragraph/CommentListAndFormular/CommentListAndFormular';
import { Rating } from 'react-simple-star-rating';

type CommentProps = BoxProps & {
  data: CommentWithChildren;
  onClickAnswer: (id: number) => void;
  openCommentIdForm: number | null;
  onCloseAnswer: () => void;
};

interface NestedCommentsProps {
  data: CommentWithChildren[];
  onClickAnswer: (id: number) => void;
  openCommentIdForm: number | null;
  onCloseAnswer: () => void;
}

const NestedComments: FC<NestedCommentsProps> = ({
  data,
  onClickAnswer,
  openCommentIdForm,
  onCloseAnswer,
}) => {
  if (!data.length) return null;

  return (
    <VStack
      pt={6}
      mt={6}
      spacing={6}
      divider={<Divider borderColor="gray.200" opacity={1} />}
      ml={9}
      borderTop="1px solid"
      borderTopColor="gray.100"
    >
      {data.map((comment) => (
        <Comment
          data={comment}
          key={comment.id}
          onClickAnswer={onClickAnswer}
          openCommentIdForm={openCommentIdForm}
          onCloseAnswer={onCloseAnswer}
        />
      ))}
    </VStack>
  );
};

export const Comment: FC<CommentProps> = ({
  data,
  onClickAnswer,
  openCommentIdForm,
  onCloseAnswer,
  ...props
}) => {
  const isFormOpen = openCommentIdForm === data.id;

  return (
    <Box width="100%" key={data.id} {...props}>
      <Flex justifyContent="space-between">
        <Flex fontWeight="bold">{data.username || 'Gast'}</Flex>
        <Box fontSize="sm">
          {format(fromUnixTime(data.created), 'dd MMMM yyyy', {
            locale: de,
          })}
        </Box>
      </Flex>

      <Box mt={2} fontSize="sm">
        {data.stars && (
          <Flex alignItems="center">
            <Box fontWeight="bold" mr={1}>
              Bewertung:
            </Box>
            <Box
              flex={1}
              __css={{
                '.star-svg': {
                  display: 'unset',
                },
              }}
            >
              <Rating initialValue={data.stars} readonly size={20} />
            </Box>
          </Flex>
        )}

        {data.dateVisit && (
          <Flex>
            <Box fontWeight="bold" mr={1}>
              Zeitpunkt der Befahrung:
            </Box>
            <Box>{data.dateVisit}</Box>
          </Flex>
        )}

        {data.vehicle && (
          <Flex>
            <Box fontWeight="bold" mr={1}>
              Befahren mit:
            </Box>
            <Box>{data.vehicle}</Box>
          </Flex>
        )}

        {data.closures && (
          <Flex>
            <Box fontWeight="bold" mr={1}>
              Sperrungen:
            </Box>
            <Box>{data.closures}</Box>
          </Flex>
        )}

        {data.tipps && (
          <Flex>
            <Box fontWeight="bold" mr={1}>
              Tipps & Hinweise:
            </Box>
            <Box>{data.tipps}</Box>
          </Flex>
        )}
      </Box>

      <Box mt={4} fontWeight="bold">
        Kommentar:
      </Box>
      <Box
        mt={1}
        dangerouslySetInnerHTML={{
          __html: data.comment,
        }}
      />

      {data.images?.length > 0 && (
        <Box mt={2} fontSize="sm">
          <Box fontWeight="bold" mb={1}>
            Anhänge:
          </Box>
          <Flex gridGap={2} flexWrap="wrap">
            {data.images.map((image, i) => (
              <Box
                as="a"
                width={
                  data.images.length === 1
                    ? '100%'
                    : {
                        base: 'calc(50% - 0.25rem)',
                        md: 'auto',
                      }
                }
                key={i}
                target="_blank"
                href={image.url}
              >
                <Img
                  htmlWidth={image.teaser.width}
                  htmlHeight={image.teaser.height}
                  src={image.teaser.url}
                  width={{
                    base: '100%',
                    md: 'auto',
                  }}
                />
              </Box>
            ))}
          </Flex>
          <Box mt={1} fontSize="xs">
            Alle Anhänge: Copyright {data.username || 'Gast'}
          </Box>
        </Box>
      )}

      <Collapse in={isFormOpen} animateOpacity>
        <Box
          bg="gray.50"
          p={6}
          mt={9}
          border="1px solid"
          borderColor="gray.400"
        >
          <Button
            size="sm"
            mb={3}
            display={{
              base: 'block',
              md: 'none',
            }}
            onClick={onCloseAnswer}
          >
            Schließen
          </Button>
          <Flex justifyContent="space-between">
            <Heading
              fontSize="xl"
              mb={4}
              fontWeight={500}
              as="h3"
              color="primary"
            >
              Auf den Kommentar von {data.username || 'Gast'} antworten
            </Heading>
            <Button
              onClick={onCloseAnswer}
              size="sm"
              mb={3}
              display={{
                base: 'none',
                md: 'block',
              }}
            >
              Schließen
            </Button>
          </Flex>

          <CommentForm parentCommentId={data.id} isAnswer />
        </Box>
      </Collapse>
      <Collapse in={!isFormOpen} animateOpacity>
        <Flex justifyContent="flex-end">
          <Button
            size="sm"
            mt={12}
            onClick={() => {
              onClickAnswer(data.id);
            }}
          >
            Antworten
          </Button>
        </Flex>
      </Collapse>

      <NestedComments
        data={data.children}
        onClickAnswer={onClickAnswer}
        openCommentIdForm={openCommentIdForm}
        onCloseAnswer={onCloseAnswer}
      />
    </Box>
  );
};
